import { IContentBlockTemplates, IRentsyncSettings } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import {
  IApiResponse,
  IMetaResponse,
} from '@/shared';
import syndicationSettings from '@/shared/services/syndication-settings';
import { StoreBaseImplementationV2 } from '../../shared/classes/store-base-implementation-v2';
import { IAction, IStateStrict } from '../../shared/interfaces/store-base';
import { DataModifyProcessor } from '../../shared/classes/data-modify-processor';
import config from '../../config';

// Base store configuration
const adminGatewayResourcePath: string = '/syndication-api/rentsync_settings';
const listViewName: string = '';
const showLogging: boolean = false;

const alias = 'rs';

const defaultGetListFilters: IQueryCommandOptions = {
  alias,
  limit: 999,
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  alias,
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IRentsyncSettings>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

interface ISyndiationSettingsState extends IStateStrict<IRentsyncSettings> {
  contentBlockTemplates: IContentBlockTemplates[];
}

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'syndication-settings/'

  state: {
    ...base.state,

    contentBlockTemplates: [],

    // custom state
    // ...{
    // },
  },

  getters: {
    ...base.getters,

    // custom getters
    ...{
      contentBlockTemplates: (state: ISyndiationSettingsState) => [
        ...state.contentBlockTemplates
          ? state.contentBlockTemplates
          : [],
      ],
    },
  },

  mutations: {
    ...base.mutations,

    // custom mutations
    ...{
      setContentBlockTemplates: (
        state: any,
        payload: IApiResponse<IContentBlockTemplates[], IMetaResponse>,
      ) => {
        state.contentBlockTemplates = [...payload.data ?? []];
      },
    },
  },

  actions: {
    ...base.actions,

    // custom actions
    ...{
      getContentBlockTemplates: async (ctx: IAction<ISyndiationSettingsState>) => {
        const result = await syndicationSettings.getContentBlockTemplates();
        ctx.commit('setContentBlockTemplates', result);
      },

      /**
     * Save many records at once, and determine POST/PUT payloads
     */
      saveMany: async (ctx: IAction<IStateStrict<IRentsyncSettings>>, payload: IRentsyncSettings[]) => {
      // Determine POST/PUT payloads
        const dataPayloads = payload.reduce((payloads: Record<'postItems'|'putItems', IRentsyncSettings[]>, item: IRentsyncSettings) => {
          const itemKey = (item.id) ? 'putItems' : 'postItems';
          payloads[`${itemKey}`].push(item);
          return payloads;
        }, {
          postItems: [],
          putItems: [],
        });
        // Make Requests
        const processor = new DataModifyProcessor<IRentsyncSettings>(`${config.adminGateway}${adminGatewayResourcePath}`);
        processor.putItems = dataPayloads.putItems;
        processor.postItems = dataPayloads.postItems;
        const results = await processor.execute();

        const putResults = results.putResult?.data ?? [];
        const postResults = results.postResult?.data ?? [];

        const unmodifiedState = (ctx.state.list?.data ?? []).filter((item) => {
          const updatedIds = putResults.map<number>((rs) => rs.id!) ?? [];
          return !updatedIds.includes(item.id!);
        });

        // Organize Updated State
        const { updatedState } = [
          ...putResults,
          ...postResults,
        ].reduce<Record<string, IRentsyncSettings[]>>((newState, item) => {
          newState.updatedState.push(item);
          return { ...newState };
        }, {
          updatedState: [],
        });

        // Update State
        ctx.commit('set', {
          data: [
            ...unmodifiedState,
            ...updatedState,
          ],
        });
      },
    },
  },
};
